import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Linkedin, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { navKeys, pathFor } from "@/navConfig";

export const Footer = () => {
  const { t, lang } = useLanguage();
  const f = t.footer;
  const [email, setEmail] = useState("");
  const [showGdpr, setShowGdpr] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("submit failed");
      toast.success(f.newsletterSuccess);
      setEmail("");
    } catch {
      toast.error(t.common.submitError);
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="relative bg-[#01154F] border-t border-white/10 pt-20 pb-10">
      <div className="container-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <img src="/obal-roku-logo.png" alt="Obal roku 2026" className="h-14 w-14 sm:h-16 sm:w-16 object-contain" />
              <span className="font-display font-bold tracking-tight text-white leading-none">
                OBAL ROKU<span className="block text-[10px] tracking-[0.3em] text-[#CEA26B] font-medium">SUMMIT 2026</span>
              </span>
            </div>
            <h3 className="mt-6 font-display font-bold text-white">
              <a
                href="https://syba.cz/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-syba-link"
                className="hover:text-[#CEA26B] transition-colors"
              >
                {f.org}
              </a>
            </h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              {f.tagline.split("Obal roku")[0]}
              <a
                href="https://obalroku.cz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-obalroku-link"
                className="text-slate-300 hover:text-[#CEA26B] underline decoration-white/30 underline-offset-2 transition-colors"
              >
                Obal roku
              </a>
              {f.tagline.split("Obal roku").slice(1).join("Obal roku")}
            </p>
          </div>

          <div>
            <h4 className="overline text-slate-500">{f.navTitle}</h4>
            <ul className="mt-5 space-y-3">
              {navKeys.map((k) => (
                <li key={k}>
                  <Link
                    to={pathFor(k, lang)}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                    data-testid={`footer-nav-${k}`}
                  >
                    {t.nav[k]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline text-slate-500">{f.newsletterTitle}</h4>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">{f.newsletterDesc}</p>
            <form onSubmit={onSubscribe} className="mt-5 flex">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setShowGdpr(true)}
                placeholder={f.newsletterPlaceholder}
                data-testid="newsletter-input"
                className="flex-1 min-w-0 bg-white/5 border border-white/15 text-white placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-[#CEA26B]"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="newsletter-submit"
                className="bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-4 flex items-center justify-center transition-colors disabled:opacity-60"
                aria-label={f.newsletterBtn}
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
            <div
              data-testid="newsletter-gdpr"
              className={`overflow-hidden transition-all duration-500 ease-out ${
                showGdpr ? "max-h-40 opacity-100 mt-3" : "max-h-0 opacity-0 mt-0"
              }`}
            >
              <p className="text-xs text-slate-500 leading-relaxed">
                {f.newsletterGdpr}{" "}
                <Link to={pathFor("privacy", lang)} className="text-slate-400 underline underline-offset-2 hover:text-[#CEA26B] transition-colors" data-testid="newsletter-privacy-link">
                  {t.privacy.title}
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-10" data-testid="footer-contacts">
          <h4 className="overline text-slate-500">{f.contactTitle}</h4>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {f.contacts.map((cnt, i) => (
              <div key={i} data-testid={`footer-contact-${i}`}>
                <h5 className="overline text-slate-500">{cnt.label}</h5>
                {cnt.name && <p className="mt-2 font-display font-bold text-white">{cnt.name}</p>}
                <a
                  href={`mailto:${cnt.email}`}
                  className="group mt-1.5 inline-flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 text-[#CEA26B] shrink-0" />
                  <span className="break-all">{cnt.email}</span>
                </a>
                {cnt.phone && (
                  <div className="mt-1.5">
                    <a
                      href={`tel:${cnt.phone.replace(/\s/g, "")}`}
                      data-testid={`footer-contact-phone-${i}`}
                      className="group inline-flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors"
                    >
                      <Phone className="h-4 w-4 text-[#CEA26B] shrink-0" />
                      <span>{cnt.phone}</span>
                    </a>
                  </div>
                )}
                {cnt.linkedin && (
                  <div className="mt-3">
                    <a
                      href={cnt.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-contact-linkedin-${i}`}
                      className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      <Linkedin className="h-4 w-4 text-[#CEA26B]" /> LinkedIn
                    </a>
                  </div>
                )}
                {cnt.social && (
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                    <a href={f.prMedia.linkedinObalRokuUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors" data-testid="footer-pr-linkedin-obalroku">
                      <Linkedin className="h-4 w-4 text-[#CEA26B]" /> Obal roku
                    </a>
                    <a href={f.prMedia.linkedinSybaUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors" data-testid="footer-pr-linkedin-syba">
                      <Linkedin className="h-4 w-4 text-[#CEA26B]" /> SYBA
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-x-5 gap-y-2">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} {f.org}. {f.rights}
            </p>
            <Link
              to={pathFor("media", lang)}
              data-testid="footer-media-link"
              className="text-xs text-slate-400 hover:text-[#CEA26B] transition-colors"
            >
              {t.mediaPage.overline}
            </Link>
            <Link
              to={pathFor("privacy", lang)}
              data-testid="footer-privacy-link"
              className="text-xs text-slate-400 hover:text-[#CEA26B] transition-colors"
            >
              {t.privacy.title}
            </Link>
          </div>
          <p className="font-display font-black text-white/5 text-2xl tracking-tighter select-none">
            OBAL ROKU SUMMIT 2026
          </p>
        </div>
      </div>
    </footer>
  );
};
